import { defineComponent as _defineComponent } from 'vue'
import { InboxFilterValues } from '@/views/careConsole/commons/model';
import { areArraysEqual } from '@/utils/array';
import CustomTableFilters from '@/components/tableWithActions/CustomTableFilters.vue';
import { isEmpty, isNil } from '@/utils/misc';
import AlertWorkflowStatusSelect from '@/components/inputs/AlertWorkflowStatusSelect.vue';
import { AlertWorkflowStatus } from '@/models/alertWorkflow';
import { dateToShortISOString, isDate } from '@/utils/dates';
import { getEnumValuesFromQueryParameterOrDefault } from '@/utils/url';
import { computed, ComputedRef } from 'vue';
import { useI18n } from '@/composables/i18n';
import { useTableFilters } from '@/composables/table-filters';


/**
 * Filters form for the alert workflows table in EServiceCenterInbox.
 */
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'InboxFilters',
  emits: ["filters-change"],
  setup(__props, { expose, emit }: { emit: ((
      e: 'filters-change',
      filters: InboxFilterValues,
      areApplying: boolean,
    ) => void), expose: any, slots: any, attrs: any }) {



const defaultFilterValues: InboxFilterValues = Object.freeze({
  subsidiaryName: [],
  customerName: [],
  equipmentNumber: [],
  equipmentName: [],
  status: [],
  assigneeName: [],
  alertName: [],
  crmCaseNumber: [],
  createdDate: null,
  processingDate: null,
  postponedDate: null,
  resolutionDate: null,
});

const areApplying: ComputedRef<boolean> = computed<boolean>(
  () =>
    areTextListFiltersApplying.value ||
    areDateRangeFiltersApplying.value ||
    !isEmpty(filters.value.status),
);

const {
  filters,
  clearAllFilters,
  onFiltersApplied,
  updateFiltersFromUrlQueryParameters,
} = useTableFilters(
  defaultFilterValues,
  filterValueSerializer,
  queryParameterDeserializer,
  areApplying,
  emit,
);
const { t } = useI18n();

function filterValueSerializer(value: any): string | string[] {
  if (typeof value[0] === 'number') {
    // AlertWorkflowStatus filter
    return value.map((valueItem: number) => AlertWorkflowStatus[valueItem]);
  } else if (value[0] instanceof Date) {
    return value.map((valueItem: Date) => dateToShortISOString(valueItem));
  } else {
    return value;
  }
}

function queryParameterDeserializer(key: keyof InboxFilterValues, value: any) {
  if (TEXT_LIST_FILTERS.includes(key)) {
    return Array.isArray(value) ? value : ([value] as any);
  } else if (DATE_RANGE_FILTERS.includes(key)) {
    return getDateRangeFilterValueFromQueryParameter(key, value) as any;
  } else if (key === 'status') {
    return getStatusFilterValueFromQueryParameter(value);
  }
}

function getDateRangeFilterValueFromQueryParameter(
  key: keyof InboxFilterValues,
  value: string | Array<string | null>,
): [Date, Date] {
  if (!Array.isArray(value) || value.length < 2) {
    return defaultFilterValues[key] as [Date, Date];
  }

  const dateRange = (value as string[])
    .slice(0, 2)
    .map((stringDate: string) => new Date(stringDate))
    .filter((date) => isDate(date));

  if (dateRange.length !== 2) {
    // Some of the dates was invalid, return default filter value
    return defaultFilterValues[key] as [Date, Date];
  }

  return dateRange as [Date, Date];
}

function getStatusFilterValueFromQueryParameter(
  queryParameterValue: string | Array<string | null>,
): AlertWorkflowStatus[] {
  return getEnumValuesFromQueryParameterOrDefault(
    queryParameterValue,
    AlertWorkflowStatus,
    defaultFilterValues.status,
  );
}

const areTextListFiltersApplying = computed<boolean>(() =>
  TEXT_LIST_FILTERS.some(
    (textListFilterKey) =>
      !areArraysEqual(
        filters.value[textListFilterKey] as string[],
        defaultFilterValues[textListFilterKey] as string[],
      ),
  ),
);

const areDateRangeFiltersApplying = computed<boolean>(() =>
  DATE_RANGE_FILTERS.some(
    (dateRangeFilterKey) => !isNil(filters.value[dateRangeFilterKey]),
  ),
);

const TEXT_LIST_FILTERS = [
  'subsidiaryName',
  'customerName',
  'equipmentNumber',
  'equipmentName',
  'assigneeName',
  'alertName',
  'crmCaseNumber',
] as Array<keyof InboxFilterValues>;
const DATE_RANGE_FILTERS = [
  'createdDate',
  'processingDate',
  'postponedDate',
  'resolutionDate',
] as Array<keyof InboxFilterValues>;

// public for testing
expose({ updateFiltersFromUrlQueryParameters });

return { __sfc: true,emit, defaultFilterValues, areApplying, filters, clearAllFilters, onFiltersApplied, updateFiltersFromUrlQueryParameters, t, filterValueSerializer, queryParameterDeserializer, getDateRangeFilterValueFromQueryParameter, getStatusFilterValueFromQueryParameter, areTextListFiltersApplying, areDateRangeFiltersApplying, TEXT_LIST_FILTERS, DATE_RANGE_FILTERS, CustomTableFilters, isNil, AlertWorkflowStatusSelect }
}

})